import { on } from '@ember/modifier';
import { action } from '@ember/object';
// eslint-disable-next-line ember/no-computed-properties-in-native-classes
import { readOnly } from '@ember/object/computed';
import { service } from '@ember/service';
import type StoreService from '@ember-data/store';
import Component from '@glimmer/component';
import { tracked } from '@glimmer/tracking';
import { Changeset } from 'ember-changeset';
import lookupValidator from 'ember-changeset-validations';
import { validateFormat } from 'ember-changeset-validations/validators';
import { all, task } from 'ember-concurrency';
import PowerSelectMultiple from 'ember-power-select/components/power-select-multiple';
import { or } from 'ember-truth-helpers';
import ConfirmDirtyTransition from 'garaje/components/confirm-dirty-transition';
import SettingsPanel from 'garaje/components/ui/classic/settings-panel';
import type ConfigModel from 'garaje/models/config';
import type EmployeeModel from 'garaje/models/employee';
import type FallbackContactModel from 'garaje/models/fallback-contact';
import type CurrentLocationService from 'garaje/services/current-location';
import type FlashMessagesService from 'garaje/services/flash-messages';
import type ImpressionsService from 'garaje/services/impressions';
import type { EmployeeSearcherTask } from 'garaje/utils/employees-searcher';
import employeesSearcherTask from 'garaje/utils/employees-searcher';
import { IMPRESSION_NAMES } from 'garaje/utils/enums';
import { parseErrorForDisplay } from 'garaje/utils/flash-promise';
import type { RecordArray } from 'garaje/utils/type-utils';
import { gt } from 'macro-decorators';

interface FallbackNotificationsComponentSignature {
  Args: {
    fallbackContacts?: FallbackContactModel[] | RecordArray<FallbackContactModel> | null;
    onUpdateFallbackContacts: (changes: { hasChanges: boolean }) => void;
    reloadFallbackContacts: () => Promise<unknown>;
  };
}

export default class FallbackNotificationsComponent extends Component<FallbackNotificationsComponentSignature> {
  @service declare currentLocation: CurrentLocationService;
  @service declare flashMessages: FlashMessagesService;
  @service declare impressions: ImpressionsService;
  @service declare store: StoreService;

  @tracked isOpen = false;
  @tracked showUnsavedEditsWarning = false;
  @tracked fallbackEmployees!: EmployeeModel[];
  @tracked _fallbackEmployees!: EmployeeModel[];
  @tracked hasChangedFallbackContacts = false;

  @gt('_fallbackEmployees.length', 0) hasSavedFallbackEmployees!: boolean;
  @readOnly('currentLocation.location.fallbackNotificationsEnabled') isEnabled!: boolean;

  get isSaveButtonDisabled(): boolean {
    return !this.hasChangedFallbackContacts || this.syncFallbackContactsTask.isRunning;
  }

  get isDisableButtonDisabled(): boolean {
    return this.disableFallbackNotifications.isRunning || this.syncFallbackContactsTask.isRunning;
  }

  constructor(owner: unknown, args: FallbackNotificationsComponentSignature['Args']) {
    super(owner, args);
    void this.mapAndLoadContactsToEmployees.perform();
  }

  @(employeesSearcherTask({
    filter: { deleted: false },
    prefix: true,
  }).restartable())
  searchEmployeesTask!: EmployeeSearcherTask;

  mapAndLoadContactsToEmployees = task(async () => {
    // Even though we pass in fallbackContacts, every other operation
    // we need to do is simpler in terms of the employee
    // load all the employees from the contacts
    const fallbackEmployees = await all(
      (this.args.fallbackContacts ?? []).map((fallbackContact) => fallbackContact.employee),
    );
    this.fallbackEmployees = fallbackEmployees;

    // '_fallbackEmployees' allows us to compare which fallback contacts are new (which
    // need to be saved) vs old (which need to be deleted) upon save
    this._fallbackEmployees = fallbackEmployees;
  });

  searchEmployeesWithoutDupsTask = task(async (term: string) => {
    const employees = await this.searchEmployeesTask.perform(term);
    const currentEmployeeIds = this.fallbackEmployees?.map((employee) => employee.id);

    return employees.filter((employee) => {
      return !currentEmployeeIds?.includes(employee.id);
    });
  });

  enableFallbackNotifications = task(async () => {
    const currentLocation = this.currentLocation.location;
    // We only want to enable this feature if we
    // have contacts
    if (this.hasSavedFallbackEmployees) {
      currentLocation.fallbackNotificationsEnabled = true;
      await currentLocation.save();
      await this.impressions.postImpression.perform(IMPRESSION_NAMES.VR_NOTIFICATIONS_FALLBACK_NOTIFICATION['ENABLED']);
    }
  });

  disableFallbackNotifications = task(async () => {
    const currentLocation = this.currentLocation.location;
    currentLocation.fallbackNotificationsEnabled = false;
    await currentLocation.save();
    await this.impressions.postImpression.perform(IMPRESSION_NAMES.VR_NOTIFICATIONS_FALLBACK_NOTIFICATION['DISABLED']);
  });

  #saveEmployeeAsFallbackContact(employee: EmployeeModel): Promise<FallbackContactModel> {
    const location = this.currentLocation.location;
    const fallbackContactRecord = this.store.createRecord('fallback-contact', {
      employee,
      location,
    });

    return fallbackContactRecord.save();
  }

  #deleteFallbackContactForEmployee(employee: EmployeeModel): Promise<FallbackContactModel> | undefined {
    const fallbackContact = this.store
      .peekAll('fallback-contact')
      .find((contact) => contact.belongsTo('employee').id() === employee.id);

    return fallbackContact?.destroyRecord();
  }

  #findNewFallbackContacts(oldContacts: EmployeeModel[], newContacts: EmployeeModel[]): EmployeeModel[] {
    return [...newContacts].filter((contact) => {
      return !oldContacts.includes(contact);
    });
  }

  #findRemovedFallbackContacts(oldContacts: EmployeeModel[], newContacts: EmployeeModel[]): EmployeeModel[] {
    return [...oldContacts].filter((contact) => {
      return !newContacts.includes(contact);
    });
  }

  syncFallbackContactsTask = task(async () => {
    const newFallbackContacts = [...this.fallbackEmployees];
    const oldFallbackContacts = [...this._fallbackEmployees];

    const newContactsToSave = this.#findNewFallbackContacts(oldFallbackContacts, newFallbackContacts);
    const oldContactsToDelete = this.#findRemovedFallbackContacts(oldFallbackContacts, newFallbackContacts);

    try {
      await all(newContactsToSave.map((employee) => this.#saveEmployeeAsFallbackContact(employee)));
      await all(oldContactsToDelete.map((employee) => this.#deleteFallbackContactForEmployee(employee)));

      this.hasChangedFallbackContacts = false;
      this._fallbackEmployees = this.fallbackEmployees;

      const contact = newContactsToSave.length > 1 ? 'contacts' : 'contact';
      this.flashMessages.showAndHideFlash('success', `Saved fallback ${contact}!`);

      if (!this.isEnabled) {
        await this.enableFallbackNotifications.perform();
      }
      this.args.onUpdateFallbackContacts({ hasChanges: false });
    } catch (error) {
      this.args.onUpdateFallbackContacts({ hasChanges: true });
      const errorText = parseErrorForDisplay(error);
      this.flashMessages.showAndHideFlash('error', errorText);
    }
    await this.args.reloadFallbackContacts();
  });

  @action
  async onContinue(): Promise<void> {
    if (this.hasChangedFallbackContacts) await this.args.reloadFallbackContacts();
    this.showUnsavedEditsWarning = false;
    this.isOpen = false;
  }

  @action
  onCancelDirtyTransition(): void {
    this.showUnsavedEditsWarning = false;
  }

  @action
  async cancel(): Promise<void> {
    if (this.hasChangedFallbackContacts) {
      this.showUnsavedEditsWarning = true;
    } else {
      await this.onContinue();
    }
  }

  @action
  async enable(): Promise<void> {
    await this.enableFallbackNotifications.perform();
    this.isOpen = true;
  }

  @action
  edit(): void {
    this.isOpen = true;
  }

  @action
  async disable(): Promise<void> {
    await this.disableFallbackNotifications.perform();
    this.isOpen = false;
  }

  @action
  setFallbackContacts(newFallbackContacts: EmployeeModel[]): void {
    this.fallbackEmployees = newFallbackContacts;
    this.hasChangedFallbackContacts = true;
    this.args.onUpdateFallbackContacts({ hasChanges: true });
  }

  <template>
    <SettingsPanel @isOpen={{this.isOpen}} data-test-fallback-notifications as |sp|>
      {{#let sp.header sp.body sp.footer as |PanelHeader PanelBody PanelFooter|}}
        <PanelHeader
          @iconUri="/assets/images/multiple-people.svg"
          @title="Fallback notifications"
          @description="Select an admin or employee to notify when a visitor does not select a host."
          @onCancel={{this.cancel}}
          as |header|
        >
          <header.controls>
            {{#if sp.isOpen}}
              <button
                data-test-save-fallback-contacts
                type="button"
                class="btn btn--small btn--brand"
                disabled={{this.isSaveButtonDisabled}}
                {{on "click" this.syncFallbackContactsTask.perform}}
              >
                {{if this.syncFallbackContactsTask.isRunning "Saving..." "Save"}}
              </button>
            {{else if this.isEnabled}}
              <button
                data-test-edit-fallback-contacts
                type="button"
                class="btn btn--small btn--brand"
                disabled={{this.enableFallbackNotifications.isRunning}}
                {{on "click" this.edit}}
              >
                {{if this.enableFallbackNotifications.isRunning "Enabling..." "Edit"}}
              </button>
            {{else}}
              <button
                data-test-enable-fallback-contacts
                type="button"
                class="btn btn--small btn--brand"
                {{on "click" this.enable}}
              >
                {{if this.enableFallbackNotifications.isRunning "Enabling..." "Enable"}}
              </button>
            {{/if}}
          </header.controls>
        </PanelHeader>
        <PanelBody as |PanelContent|>
          <PanelContent @style="inset" class="mt-8 mr-16">
            <div class="flow-root row">
              <div class="float-left box-border w-10/12">
                <label id="fallback-contacts">Fallback notification contact(s)</label>
                <div data-test-fallback-contacts-select>
                  <PowerSelectMultiple
                    @placeholder="Choose a contact"
                    @ariaDescribedBy="fallback-contacts"
                    @allowClear={{true}}
                    @selected={{this.fallbackEmployees}}
                    @searchEnabled={{true}}
                    @search={{this.searchEmployeesWithoutDupsTask.perform}}
                    @onChange={{this.setFallbackContacts}}
                    @disabled={{this.mapAndLoadContactsToEmployees.isRunning}}
                    as |employee|
                  >
                    {{employee.fullName}}
                  </PowerSelectMultiple>
                </div>
              </div>
            </div>
          </PanelContent>
          {{#if (or this.isEnabled this.disableFallbackNotifications.isRunning)}}
            <PanelFooter>
              <button
                data-test-disable-fallback-contacts
                type="button"
                class="btn btn--small btn--outlineFill"
                disabled={{this.isDisableButtonDisabled}}
                {{on "click" this.disable}}
              >
                {{if this.disableFallbackNotifications.isRunning "Disabling..." "Disable"}}
              </button>
            </PanelFooter>
          {{/if}}
        </PanelBody>
      {{/let}}
    </SettingsPanel>
    {{#if this.showUnsavedEditsWarning}}
      <ConfirmDirtyTransition
        @headerMessage="You have unsaved changes"
        @bodyMessage="You will lose any unsaved changes. Are you sure you want to continue?"
        @confirm={{this.onContinue}}
        @cancel={{this.onCancelDirtyTransition}}
      />
    {{/if}}
  </template>
}

interface AdditionalEmailsSignature {
  Args: {
    config: ConfigModel;
    onUpdate: (changes: { hasChanges: boolean }) => void;
  };
}

const EmailValidations = {
  email: [
    validateFormat({
      type: 'email',
      allowBlank: false,
      message: 'Enter a valid email address',
    }),
  ],
};

export class AdditionalNotificationEmails extends Component<AdditionalEmailsSignature> {
  @service declare flashMessages: FlashMessagesService;
  @service declare impressions: ImpressionsService;

  @tracked isOpen = false;
  @tracked showUnsavedEditsWarning = false;
  @tracked hasChanges = false;
  @tracked emailsInput = '';

  get hasSavedEmails(): boolean {
    const emails = this.args.config.emailsToNotifyOnSignIn || [];
    return emails.length > 0;
  }

  get isEnabled(): boolean {
    const enabled = this.args.config.notifyEmailsOnSignInEnabled || false;
    return enabled;
  }

  get emailValidationError(): string {
    const emails = this.emailsInput
      .split(',')
      .map((e) => e.trim())
      .filter((e) => e.length > 0);

    for (const email of emails) {
      const validator = lookupValidator(EmailValidations);
      const changeset = Changeset({ email }, validator, EmailValidations);
      void changeset.validate();

      if (changeset.error?.['email']) {
        return 'Enter a valid email address';
      }
    }
    return '';
  }

  get isSaveButtonDisabled(): boolean {
    return !this.hasChanges || this.saveEmailsTask.isRunning || !!this.emailValidationError;
  }

  get isDisableButtonDisabled(): boolean {
    return this.disableNotifications.isRunning || this.saveEmailsTask.isRunning;
  }

  constructor(owner: unknown, args: AdditionalEmailsSignature['Args']) {
    super(owner, args);

    const existingEmails = args.config.emailsToNotifyOnSignIn || [];
    this.emailsInput = existingEmails.join(', ');
  }

  enableNotifications = task(async () => {
    if (this.hasSavedEmails || (this.emailsInput.trim().length > 0 && !this.emailValidationError)) {
      this.args.config.notifyEmailsOnSignInEnabled = true;
      await this.args.config.save();

      this.args.config.notifyEmailsOnSignInEnabled = true;

      await this.impressions.postImpression.perform(
        IMPRESSION_NAMES.VR_NOTIFICATIONS_ADDITIONAL_EMAIL_RECIPIENTS['ENABLED'],
      );
    }
  });

  disableNotifications = task(async () => {
    this.args.config.notifyEmailsOnSignInEnabled = false;
    await this.args.config.save();

    await this.impressions.postImpression.perform(
      IMPRESSION_NAMES.VR_NOTIFICATIONS_ADDITIONAL_EMAIL_RECIPIENTS['DISABLED'],
    );
  });

  saveEmailsTask = task(async () => {
    try {
      const emailsArray = this.emailsInput
        .split(',')
        .map((email) => email.trim())
        .filter((email) => email.length > 0);

      this.args.config.emailsToNotifyOnSignIn = emailsArray;
      this.args.config.notifyEmailsOnSignInEnabled = true;

      await this.args.config.save();

      this.args.config.emailsToNotifyOnSignIn = emailsArray;
      this.args.config.notifyEmailsOnSignInEnabled = true;

      this.hasChanges = false;

      const emailText = emailsArray.length > 1 ? 'emails' : 'email';
      this.flashMessages.showAndHideFlash('success', `Saved notification ${emailText}!`);

      await this.impressions.postImpression.perform(
        IMPRESSION_NAMES.VR_NOTIFICATIONS_ADDITIONAL_EMAIL_RECIPIENTS['ENABLED'],
      );

      this.args.onUpdate({ hasChanges: false });
    } catch (error) {
      this.args.onUpdate({ hasChanges: true });
      const errorText = parseErrorForDisplay(error);
      this.flashMessages.showAndHideFlash('error', errorText);
    }
  });

  @action
  onContinue(): void {
    if (this.hasChanges) {
      const existingEmails = this.args.config.emailsToNotifyOnSignIn || [];
      this.emailsInput = existingEmails.join(', ');
      this.hasChanges = false;
    }
    this.showUnsavedEditsWarning = false;
    this.isOpen = false;
  }

  @action
  onCancelDirtyTransition(): void {
    this.showUnsavedEditsWarning = false;
  }

  @action
  cancel(): void {
    if (this.hasChanges) {
      this.showUnsavedEditsWarning = true;
    } else {
      this.onContinue();
    }
  }

  @action
  async enable(): Promise<void> {
    await this.enableNotifications.perform();
    this.isOpen = true;
  }

  @action
  edit(): void {
    this.isOpen = true;
  }

  @action
  async disable(): Promise<void> {
    await this.disableNotifications.perform();
    this.isOpen = false;
  }

  @action
  updateEmailInput(event: Event): void {
    const target = event.target as HTMLInputElement;
    const value = target.value;

    this.emailsInput = value;
    this.hasChanges = true;
    this.args.onUpdate({ hasChanges: true });
  }

  <template>
    <SettingsPanel @isOpen={{this.isOpen}} data-test-additional-notification-emails as |sp|>
      {{#let sp.header sp.body sp.footer as |PanelHeader PanelBody PanelFooter|}}
        <PanelHeader
          @iconUri="/assets/images/features/notifications.svg"
          @title="Additional recipients for host notifications"
          @description="Hosts will still get notifications. The addresses listed here will also receive them separately."
          @onCancel={{this.cancel}}
          as |header|
        >
          <header.controls>
            {{#if sp.isOpen}}
              <button
                data-test-save-additional-emails
                type="button"
                class="btn btn--small btn--brand"
                disabled={{this.isSaveButtonDisabled}}
                {{on "click" this.saveEmailsTask.perform}}
              >
                {{if this.saveEmailsTask.isRunning "Saving..." "Save"}}
              </button>
            {{else if this.isEnabled}}
              <button
                data-test-edit-additional-emails
                type="button"
                class="btn btn--small btn--brand"
                disabled={{this.enableNotifications.isRunning}}
                {{on "click" this.edit}}
              >
                {{if this.enableNotifications.isRunning "Enabling..." "Edit"}}
              </button>
            {{else}}
              <button
                data-test-enable-additional-emails
                type="button"
                class="btn btn--small btn--brand"
                {{on "click" this.enable}}
              >
                {{if this.enableNotifications.isRunning "Enabling..." "Enable"}}
              </button>
            {{/if}}
          </header.controls>
        </PanelHeader>
        <PanelBody as |PanelContent|>
          <PanelContent @style="inset" class="mt-8 mr-16">
            <div class="flow-root row">
              <div class="float-left box-border w-10/12">
                <label id="additional-notification-emails">Email address(es)</label>
                <div data-test-additional-emails-input>
                  <input
                    type="text"
                    class="form-control {{if this.emailValidationError 'is-invalid'}}"
                    placeholder="Enter email addresses separated by commas (e.g., user1@example.com, user2@example.com)"
                    value={{this.emailsInput}}
                    {{on "input" this.updateEmailInput}}
                    aria-labelledby="additional-notification-emails"
                  />
                  {{#if this.emailValidationError}}
                    <div class="invalid-feedback d-block">{{this.emailValidationError}}</div>
                  {{/if}}
                </div>
              </div>
            </div>
          </PanelContent>
          {{#if (or sp.isOpen this.disableNotifications.isRunning)}}
            <PanelFooter>
              <button
                data-test-disable-additional-emails
                type="button"
                class="btn btn--small btn--outlineFill"
                disabled={{this.isDisableButtonDisabled}}
                {{on "click" this.disable}}
              >
                {{if this.disableNotifications.isRunning "Disabling..." "Disable"}}
              </button>
            </PanelFooter>
          {{/if}}
        </PanelBody>
      {{/let}}
    </SettingsPanel>
    {{#if this.showUnsavedEditsWarning}}
      <ConfirmDirtyTransition
        @headerMessage="You have unsaved changes"
        @bodyMessage="You will lose any unsaved changes. Are you sure you want to continue?"
        @confirm={{this.onContinue}}
        @cancel={{this.onCancelDirtyTransition}}
      />
    {{/if}}
  </template>
}
